import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceLog, 
  LogMedia, 
  LogChecklist, 
  LogStats, 
  LogFilters 
} from '../types/maintenance-logs.types';

export const MaintenanceLogsService = {
  /**
   * Lấy danh sách nhật ký bảo dưỡng
   */
  async getLogs(companyId: string, filters: LogFilters = {}): Promise<{ data: MaintenanceLog[], total: number }> {
    let query = supabase
      .from('maintenance_logs')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name),
        work_order:work_orders(id, work_order_code, title),
        worker:user_profiles!worker_id(id, full_name, avatar_url:avatar_path),
        team:teams(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.zoneId) query = query.eq('zone_id', filters.zoneId);
    if (filters.workerId) query = query.eq('worker_id', filters.workerId);
    if (filters.dateRange) {
      query = query.gte('log_date', filters.dateRange.start).lte('log_date', filters.dateRange.end);
    }
    if (filters.search) {
      query = query.or(`task_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    query = query.order('log_date', { ascending: false }).order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceLog[],
      total: count || 0
    };
  },

  /**
   * Chi tiết nhật ký
   */
  async getLogDetail(id: string): Promise<MaintenanceLog> {
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name),
        work_order:work_orders(id, work_order_code, title),
        worker:user_profiles!worker_id(id, full_name, avatar_url:avatar_path),
        team:teams(id, name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenanceLog;
  },

  /**
   * Lấy media của nhật ký
   */
  async getLogMedia(logId: string): Promise<LogMedia[]> {
    const { data, error } = await supabase
      .from('maintenance_log_media')
      .select('*')
      .eq('log_id', logId)
      .order('captured_at', { ascending: true });

    if (error) throw error;
    return data as LogMedia[];
  },

  /**
   * Thống kê nhật ký
   */
  async getLogStats(companyId: string): Promise<LogStats> {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select('status_code, duration_minutes, quantity_actual, log_date')
      .eq('company_id', companyId);

    if (error) throw error;

    const todayItems = data.filter(l => l.log_date === today);
    const approved = data.filter(l => l.status_code === 'approved').length;
    const totalMinutes = data.reduce((sum, l) => sum + (l.duration_minutes || 0), 0);

    return {
      todayLogs: todayItems.length,
      completedQuantity: data.reduce((sum, l) => sum + (l.quantity_actual || 0), 0),
      totalWorkHours: Math.round(totalMinutes / 60),
      approvalRate: data.length > 0 ? (approved / data.length) * 100 : 0,
    };
  },

  /**
   * Duyệt/Từ chối nhật ký
   */
  async updateStatus(id: string, statusCode: 'approved' | 'rejected' | 'submitted'): Promise<void> {
    const { error } = await supabase
      .from('maintenance_logs')
      .update({ status_code: statusCode })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * CRUD
   */
  async createLog(data: Partial<MaintenanceLog>): Promise<MaintenanceLog> {
    const { data: result, error } = await supabase
      .from('maintenance_logs')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceLog;
  }
};

